import { useState, useEffect } from 'react';

import { LabellerFactViewer } from './shared/LabellerFactViewer';
import { useIncludedFacts } from './shared/useIncludedFacts';
import { useClusterFacts } from '@/api/mutations/useClusterFacts';
import { Label } from '@/components/atoms/label';
import { Button } from '@/components/atoms/new-button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/atoms/ShadCnSelect';
import { Textarea } from '@/components/atoms/textarea';
import CustomModal from '@/components/molecules/Modals/CustomModal';
import { FilterOptions } from '@/constants/filterOptions';
import { Classification, Relevance } from '@/types';

interface ClusterFactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  onSuccess: () => void;
}

export const ClusterFactsModal = ({ isOpen, onClose, caseId, onSuccess }: ClusterFactsModalProps) => {
  const { facts, currentFactIndex, handlePrevFact, handleNextFact } = useIncludedFacts(caseId, isOpen);
  const [description, setDescription] = useState('');
  const [author, setAuthor] = useState('');
  const [factDateText, setFactDateText] = useState('');
  const [significance, setSignificance] = useState('');
  const [topic, setTopic] = useState('');
  const [relevance, setRelevance] = useState<Relevance>(Relevance.Relevant);
  const [classification, setClassification] = useState<Classification>(Classification.Primary);

  useEffect(() => {
    if (isOpen) {
      setDescription('');
      setAuthor('');
      setFactDateText('');
      setSignificance('');
      setTopic('');
      setRelevance(Relevance.Relevant);
      setClassification(Classification.Primary);
    }
  }, [isOpen]);

  const { mutate: clusterFacts, isLoading } = useClusterFacts(caseId, {
    onSuccess: () => {
      onSuccess();
      onClose();
    },
  });

  const handleCluster = () => {
    if (!description.trim()) {
      return;
    }

    clusterFacts({
      factIds: facts.map((f) => f.id),
      description,
      author: author || undefined,
      factDateText: factDateText || undefined,
      significance: significance || undefined,
      topic: topic || undefined,
      relevance,
      classification,
      issues: [], // Could be extended to include issues/themes
    });
  };

  return (
    <CustomModal
      isOpen={isOpen}
      handleClose={onClose}
      maxWidth="calc(100vw - 4rem)"
      content={
        <>
          <div className="flex max-h-[70vh]">
            <LabellerFactViewer
              facts={facts}
              currentFactIndex={currentFactIndex}
              onPrevious={handlePrevFact}
              onNext={handleNextFact}
            />

            <div className="w-1/2 overflow-y-auto p-6">
              <h3 className="mb-4 text-lg font-semibold">New Clustered Fact</h3>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter the consolidated fact description"
                    className="mt-1"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="author">Author</Label>
                    <input
                      id="author"
                      type="text"
                      value={author}
                      onChange={(e) => setAuthor(e.target.value)}
                      placeholder="Enter author"
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <Label htmlFor="factDateText">Date</Label>
                    <input
                      id="factDateText"
                      type="text"
                      value={factDateText}
                      onChange={(e) => setFactDateText(e.target.value)}
                      placeholder="Enter date"
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="topic">Topic</Label>
                  <input
                    id="topic"
                    type="text"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="Enter topic"
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="significance">Significance</Label>
                  <Textarea
                    id="significance"
                    value={significance}
                    onChange={(e) => setSignificance(e.target.value)}
                    placeholder="Enter significance"
                    className="mt-1"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="relevance">Relevance</Label>
                    <Select value={relevance} onValueChange={(value) => setRelevance(value as Relevance)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FilterOptions.relevance.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="classification">Classification</Label>
                    <Select
                      value={classification}
                      onValueChange={(value) => setClassification(value as Classification)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FilterOptions.classification.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCluster} disabled={!description.trim() || isLoading}>
              {isLoading ? 'Clustering...' : 'Cluster Facts'}
            </Button>
          </div>
        </>
      }
    />
  );
};
